import React, { useState, useEffect } from 'react';
import { X, History, FileText, Edit, Move, Copy, Trash2, Upload, RotateCcw, Clock, Share2, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import api from '../../utils/api';
import FileIcon from './FileIcon';

/**
 * FileHistorySidebar Component
 * Shows timeline of file changes/activity
 */
const FileHistorySidebar = ({ file, isOpen, onClose }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && file) {
            fetchHistory();
        }
    }, [isOpen, file]);

    const fetchHistory = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/files/activity/${file.id}`);
            setHistory(response.data.history || []);
        } catch (error) {
            console.error('Error fetching file history:', error);
        } finally {
            setLoading(false);
        }
    };

    const getActivityIcon = (action) => {
        switch (action) {
            case 'upload': return <Upload size={14} className="text-blue-500" />;
            case 'edit': return <Edit size={14} className="text-amber-500" />;
            case 'rename': return <FileText size={14} className="text-indigo-500" />; // Renaming
            case 'move': return <Move size={14} className="text-purple-500" />;
            case 'copy': return <Copy size={14} className="text-green-500" />;
            case 'delete': return <Trash2 size={14} className="text-red-500" />;
            case 'restore': return <RotateCcw size={14} className="text-emerald-500" />;
            case 'version': return <History size={14} className="text-blue-600" />;
            case 'ai_index': return <Sparkles size={14} className="text-purple-600" />;
            case 'schedule': return <Clock size={14} className="text-orange-500" />;
            default: return <FileText size={14} className="text-gray-500" />;
        }
    };

    const getActivityText = (item) => {
        const details = item.details || {};
        switch (item.action) {
            case 'upload': return 'Uploaded file';
            case 'edit': return 'Updated file metadata';
            case 'rename': return `Renamed from "${details.old_name || '?'}" to "${details.new_name || '?'}"`;
            case 'move': return `Moved from ${details.from_folder || 'root'} to ${details.to_folder || 'root'}`;
            case 'copy': return 'Duplicated file';
            case 'delete': return 'Deleted file';
            case 'restore': return 'Restored file';
            case 'version': return `Uploaded version ${details.version || '?'}`;
            case 'ai_index': return details.indexed ? 'Indexed for AI' : 'Removed from AI index';
            case 'schedule': return 'Scheduled for release';
            default: return item.action.charAt(0).toUpperCase() + item.action.slice(1);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 border-l border-gray-100 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100">
                        {file && <FileIcon fileType={file.file_type} size={20} />}
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-800 truncate max-w-[200px]" title={file?.file_name}>
                            {file?.file_name || 'File History'}
                        </h3>
                        <p className="text-xs text-gray-500">Activity Log</p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X size={20} />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : history.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <History size={48} className="mx-auto mb-3 text-gray-300" />
                        <p>No activity recorded yet</p>
                    </div>
                ) : (
                    <div className="relative pl-4 border-l-2 border-gray-100 space-y-8">
                        {history.map((item, index) => (
                            <div key={item.id || index} className="relative">
                                {/* Timeline dot */}
                                <div className="absolute -left-[25px] mt-1.5 w-4 h-4 rounded-full bg-white border-2 border-primary shadow-sm flex items-center justify-center">
                                    <div className="w-1.5 h-1.5 rounded-full bg-primary/50"></div>
                                </div>

                                <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1.5 bg-gray-50 rounded-lg">
                                                {getActivityIcon(item.action)}
                                            </div>
                                            <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                                {item.action.replace('_', ' ')}
                                            </span>
                                        </div>
                                        <span className="text-xs text-gray-400">
                                            {format(new Date(item.created_at), 'MMM d, h:mm a')}
                                        </span>
                                    </div>

                                    <p className="text-sm text-gray-800 font-medium mb-2">
                                        {getActivityText(item)}
                                    </p>

                                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-50">
                                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-[10px] font-bold text-indigo-700">
                                            {item.performed_by_name?.charAt(0) || 'U'}
                                        </div>
                                        <span className="text-xs text-gray-500">
                                            by <span className="text-gray-900 font-medium">{item.performed_by_name || 'Unknown User'}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50/30 text-center text-xs text-gray-400">
                History tracks all modifications to this file
            </div>
        </div>
    );
};

export default FileHistorySidebar;
